import React, { useCallback } from 'react';
import {
  Avatar,
  IconButton,
  ImageListItemBar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Typography,
} from '@material-ui/core';
import { v4 as uuidv4 } from 'uuid';
import DeleteIcon from '@material-ui/icons/Delete';
import InsertDriveFileOutlinedIcon from '@material-ui/icons/InsertDriveFileOutlined';

const checkBinFileIsImage = (file: File) =>
  file && file.type.split('/')[0] === 'image';

const checkLinkIsImage = (link: string) =>
  link.endsWith('.png') || link.endsWith('.jpeg') || link.endsWith('.jpg');

function checkIsBinFile(file: File | string): file is File {
  return (file as File) instanceof File;
}

function formatBytes(bytes: number, decimals = 2) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const resSize = parseFloat((bytes / k ** i).toFixed(dm));
  return `${resSize} ${sizes[i]}`;
}

type PFilePreviews = {
  files: (File | string)[];
  onRemove: (files: (File | string)[]) => void;
  onDelete?: () => void;
};

export const FilePreviews: React.FC<PFilePreviews> = ({
  files,
  onRemove,
  onDelete,
}) => {
  const handleRemove = useCallback(
    (fileIndex: number) => {
      if (onDelete) {
        onDelete();
      }
      onRemove(
        files.filter((_, index) => {
          return index !== fileIndex;
        }),
      );
    },
    [files, onRemove],
  );

  return (
    <List style={{ width: '100%' }}>
      {files.map((file, fileIndex) => {
        let isImage;
        let fileName;
        let filePath;
        let fileSize;

        if (checkIsBinFile(file)) {
          isImage = checkBinFileIsImage(file);
          fileName = file.name;
          filePath = URL.createObjectURL(file);
          fileSize = formatBytes(file.size);
        } else {
          isImage = checkLinkIsImage(file);
          fileName = file;
          filePath = file;
          fileSize = 'Unknown file size';
        }

        return (
          <ListItem key={uuidv4()} style={{ justifyContent: 'center' }}>
            {isImage ? (
              <>
                <img src={filePath} alt="img" style={{ maxHeight: '250px' }} />
                <ImageListItemBar
                  title={<Typography noWrap>{fileName}</Typography>}
                  actionIcon={
                    <IconButton onClick={() => handleRemove(fileIndex)}>
                      <DeleteIcon style={{ color: 'white' }} />
                    </IconButton>
                  }
                />
              </>
            ) : (
              <>
                <ListItemAvatar>
                  <Avatar>
                    <InsertDriveFileOutlinedIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography style={{ width: '80%' }} noWrap>
                      {fileName}
                    </Typography>
                  }
                  secondary={fileSize}
                />
                <ListItemSecondaryAction>
                  <IconButton
                    onClick={() => handleRemove(fileIndex)}
                    edge="end"
                    aria-label="delete">
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </>
            )}
          </ListItem>
        );
      })}
    </List>
  );
};
